import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtErr } from "../../../api";
import { useAuth } from "../../../AuthContext";
import { ChangePassword } from "../../../components/ChangePassword";
import { PrivacyPanel } from "../../../components/PrivacyPanel";
import { LegalLinks } from "../../../components/LegalLinks";
import { toast } from "sonner";

export default function SettingsAccount() {
  const { user, setUser, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const [confirmDeact, setConfirmDeact] = useState(false);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      const t = setInterval(async () => {
        try {
          const r = await api.get(`/billing/status/${sid}`);
          if (r.data.subscription_status === "active") {
            clearInterval(t);
            setParams({});
            toast.success("Subscription active — welcome aboard!");
            const me = await api.get("/auth/me");
            setUser(me.data);
          }
        } catch { /* billing poll retry */ }
      }, 2000);
      setTimeout(() => clearInterval(t), 30000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only billing return poll by design
  }, []);

  const subscribe = async (plan = "monthly") => {
    try {
      const r = await api.post("/billing/checkout", { origin_url: window.location.origin, plan });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-8">
      <section id="set-billing" className="card-flat space-y-3 p-6" data-testid="billing-settings">
        <p className="label-caps">RPM Field Pro — billed by Veloktify</p>
        {user?.subscription_status === "active" ? (
          <>
            <p className="rounded-md bg-primary/10 p-4 text-sm font-bold text-primary" data-testid="subscription-active">Active — RPM Field Pro. Thanks!</p>
            <button data-testid="manage-subscription-btn" onClick={async () => {
              try {
                const r = await api.post("/billing/portal", { origin_url: window.location.origin });
                window.location.href = r.data.portal_url;
              } catch (e) { toast.error(fmtErr(e)); }
            }} className="h-14 w-full rounded-md border border-border font-bold">
              Manage subscription — update card or cancel
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {params.get("billing") === "required" ? "Your trial has ended. " : ""}
              Free for 30 days, no card to start{user?.trial_end ? ` (trial ends ${new Date(user.trial_end).toLocaleDateString()})` : ""}.
            </p>
            <button data-testid="subscribe-btn" onClick={() => subscribe("monthly")} className="btn-primary-big !h-14 !text-base">Subscribe — $29/mo intro (first 12 months), then $79/mo</button>
            <button data-testid="subscribe-yearly-btn" onClick={() => subscribe("yearly")} className="h-14 w-full rounded-md border-2 border-primary text-base font-bold text-primary">Subscribe yearly — $790 CAD/year</button>
          </>
        )}
      </section>

      <section id="set-password" className="card-flat space-y-3 p-6" data-testid="password-settings">
        <p className="label-caps">Password</p>
        <ChangePassword />
      </section>

      <div id="set-privacy" data-testid="privacy-settings"><PrivacyPanel /></div>

      <section id="set-delete" className="card-flat space-y-3 p-6" data-testid="account-settings">
        <p className="label-caps">Account</p>
        <p className="text-sm text-muted-foreground">Deactivating signs you out and disables this login. Your data is preserved — contact Veloktify to reactivate.</p>
        {!confirmDeact ? (
          <button data-testid="deactivate-btn" onClick={() => setConfirmDeact(true)} className="h-12 w-full rounded-md border border-destructive text-sm font-bold text-destructive">Deactivate my account</button>
        ) : (
          <button data-testid="deactivate-confirm" onClick={async () => {
            try { await api.post("/users/me/deactivate"); logout(); }
            catch (e) { toast.error(fmtErr(e)); setConfirmDeact(false); }
          }} className="h-12 w-full rounded-md bg-destructive text-sm font-bold text-destructive-foreground">Yes — deactivate my account</button>
        )}
      </section>

      <section id="set-legal" className="card-flat space-y-2 p-6" data-testid="legal-settings">
        <p className="label-caps">Legal</p>
        <LegalLinks className="!text-left" />
        <p className="text-xs text-muted-foreground" data-testid="terms-accepted">
          {user?.accepted_terms_at
            ? `You accepted version ${user.accepted_terms_version || "—"} on ${new Date(user.accepted_terms_at).toLocaleDateString()}`
            : "Terms acceptance: not recorded (account predates tracking)"}
        </p>
      </section>
    </div>
  );
}
